import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import {
  BarSeriesOption,
  ECElementEvent,
  ECharts,
  EChartsOption,
  GridComponentOption,
  LegendComponentOption,
  Payload,
  TitleComponentOption,
  ToolboxComponentOption,
  TooltipComponentOption,
  XAXisComponentOption,
  YAXisComponentOption,

} from 'echarts';
import { NgxEchartsDirective, ThemeOption } from 'ngx-echarts';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-bar-echart',
  templateUrl: './bar-echart.component.html',
  styleUrl: './bar-echart.component.scss'
})
export class BarEchartComponent implements OnInit, OnDestroy {

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this._chartInstance) {
      this._chartInstance.resize();
    }
  }

  private _chartInstance: ECharts;

  @ViewChild(NgxEchartsDirective) plotRef: NgxEchartsDirective;

  @Input()
  set title(value: TitleComponentOption) {
    (<TitleComponentOption>this.options.title) = { ...value };
    this._plot();
  }

  @Input()
  set legend(value: LegendComponentOption) {
    (<LegendComponentOption>this.options.legend) = { ...value };
    this._plot();
  }

  @Input()
  set series(list: BarSeriesOption[]) {
    (<BarSeriesOption[]>this.options.series) = list?.map(
      (data) => {
        return {
          ...data,
          type: 'bar'
        }
      });
    this._plot();
  }

  @Input()
  set tooltip(value: TooltipComponentOption) {
    (<TooltipComponentOption>this.options.tooltip) = { ...value };
    this._plot();
  }

  @Input()
  set xAxis(value: XAXisComponentOption) {
    (<XAXisComponentOption>this.options.xAxis) = { ...value };
    this._plot();
  }

  @Input()
  set yAxis(value: YAXisComponentOption[]) {  // Changed to array for double y-axis
    (<YAXisComponentOption[]>this.options.yAxis) = value;
    this._plot();
  }

  @Input()
  set grid(value: GridComponentOption) {
    (<GridComponentOption>this.options.grid) = { ...value };
    this._plot();
  }

  @Input() merge: EChartsOption;

  @Input() autoResize: boolean;

  @Input() initOpts: {
    devicePixelRatio?: number;
    renderer?: string;
    width?: number | string;
    height?: number | string;
    locale?: string;
  } = {
    width: 'auto',
    height: 'auto'
  };

  @Input() theme: string | ThemeOption;

  @Input() 
  set dispatchAction(payload: Payload) {
    this._chartRenderedSubscription = this.chartRendered
    .subscribe({
      next: () => {
        this._chartInstance?.dispatchAction(payload);
      },
      error: (errorResponse: HttpErrorResponse) => { }
    });
  }
  
  @Input()
  set toolbox(value: ToolboxComponentOption) {
    (<Record<string, any>>this.options.toolbox) = { ...value };
    this._plot();
  }


  @Input() loadingOpts: object = null; // Please refer https://github.com/xieziyu/ngx-echarts or https://echarts.apache.org/en/api.html#echartsInstance.showLoading

  @Output() chartInit = new Subject<ECharts>();

  @Output() chartClick = new Subject<ECElementEvent>();

  @Output() chartDblClick = new Subject<ECElementEvent>();

  @Output() chartMouseDown = new Subject<ECElementEvent>();

  @Output() chartMouseMove = new Subject<ECElementEvent>();

  @Output() chartMouseUp = new Subject<ECElementEvent>();

  @Output() chartMouseOver = new Subject<ECElementEvent>();
  
  @Output() chartMouseOut = new Subject<ECElementEvent>();

  @Output() chartRendered = new Subject<boolean>();

  @Output() chartFinished = new Subject<boolean>();

  @Output() dataZoom = new Subject<ECElementEvent>();

  options: EChartsOption = {
    title: {} as TitleComponentOption,
    legend: {} as LegendComponentOption,
    series: [] as BarSeriesOption[],
    tooltip: {} as TooltipComponentOption,
    xAxis: {} as XAXisComponentOption,
    yAxis: [] as YAXisComponentOption[],  // Initialize as array
    grid: {} as GridComponentOption,
  };

  loading: boolean = false;

  // Subscription
  private _chartRenderedSubscription: Subscription;

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this._chartRenderedSubscription?.unsubscribe();
  }

  ngOnChanges() : any {
  }

  /**
   * Determines whether chart init on
   * @param value
   */
  onChartInit(value: ECharts) {
    this._chartInstance = value;
    this.chartInit.next(this._chartInstance);
  }

  /**
   * Determines whether chart click on
   * @param value
   */
  onChartClick(value: unknown) {
    this.chartClick.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart double click on
   * @param value
   */
  onChartDblClick(value: unknown) {
    this.chartDblClick.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse down on
   * @param value
   */
  onChartMouseDown(value: unknown) {
    this.chartMouseDown.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse move on
   * @param value
   */
  onChartMouseMove(value: unknown) {
    this.chartMouseMove.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse up on
   * @param value
   */
  onChartMouseUp(value: unknown) {
    this.chartMouseUp.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse over on
   * @param value
   */
  onChartMouseOver(value: unknown) {
    this.chartMouseOver.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse out on
   * @param value
   */
  onChartMouseOut(value: unknown) {
    this.chartMouseOut.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart rendered on
   */
  onChartRendered() {
    this.chartRendered.next(true);
    this._setLoader(false);
  }

  /**
   * Determines whether chart finished on
   */
  onChartFinished() {
    this.chartFinished.next(true);
  }
  
    /**
   * Determines whether data zoom on
   * @param value 
   */
     onDataZoom(value: unknown) {
      this.dataZoom.next(value as ECElementEvent);
    }

  /**
   * Plots tree component
   */
  private _plot() {
    this.plotRef?.refreshChart();
    this._setLoader(true);
  }

  /**
   * Sets loader
   * @param option
   */
  private _setLoader(option: boolean) {
    this.loading = option;
  }
}
